import React from 'react';
import { NumberFormatOptions } from '@formatjs/ecma402-abstract';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import Box from 'ui-box';
import {
  Card,
  defaultFills,
  Heading,
  ICardProps,
  majorScale,
} from '../../npaper';
import { BarTooltipDatum, ResponsiveBar } from '@nivo/bar';
import moment from 'moment';

export interface IGoalCardProps extends ICardProps {
  value: number;
  goal: number;
  description: string;
  unit?: string;
  formatting?: NumberFormatOptions & { format?: string };
}

export const GoalCard: React.FC<IGoalCardProps> = ({
  value,
  description,
  goal,
  unit,
  formatting,
  ...props
}) => {
  const progress = value / goal;
  const rotation = Math.min(Math.max(progress * 120, 5), 175);

  return (
    <Card
      minHeight="200px"
      padding={majorScale(2)}
      {...props}
      display="flex"
      flexDirection="column"
    >
      <Box is="p">{description}</Box>
      <Heading is="p" size={400} className="font-mono medium large">
        <FormattedNumber
          format="short"
          maximumFractionDigits={0}
          minimumFractionDigits={0}
          {...formatting}
          value={value}
        />{' '}
        <span className="p muted">
          (
          <FormattedNumber
            format="short"
            maximumFractionDigits={0}
            minimumFractionDigits={0}
            {...formatting}
            value={goal}
          />{' '}
          <FormattedMessage defaultMessage="goal" />)
        </span>
      </Heading>
      <Box
        textAlign="center"
        flexGrow={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <svg width="182px" height="100px" viewBox="0 0 182 100" version="1.1">
          <g id="Group">
            <mask id="mask">
              <path
                fill="white"
                d="M91.4041559,0 C141.159489,0 181.588363,39.9313202 182.391964,89.4951486 L182.404156,91 L150.404156,91 C150.404156,58.4151998 123.988956,32 91.4041559,32 C58.8193556,32 32.4041559,58.4151998 32.4041559,91 L0.404155872,91 L0.416347272,89.4951486 C1.21994921,39.9313202 41.6488228,0 91.4041559,0 Z"
                id="path-1"
              />
            </mask>
            <path
              fill="#FFA400"
              mask="url(#mask)"
              d="M91.4041559,0 C141.159489,0 181.588363,39.9313202 182.391964,89.4951486 L182.404156,91 L150.404156,91 C150.404156,58.4151998 123.988956,32 91.4041559,32 C58.8193556,32 32.4041559,58.4151998 32.4041559,91 L0.404155872,91 L0.416347272,89.4951486 C1.21994921,39.9313202 41.6488228,0 91.4041559,0 Z"
              id="path-1"
            />
            <g mask="url(#mask)">
              <path
                d="M52,68.25 C101.755333,68.25 142.184207,108.18132 142.987809,157.745149 L143,159.25 L111,159.25 C111,126.6652 84.5848002,100.25 52,100.25 C19.4151998,100.25 -7,126.6652 -7,159.25 L-39,159.25 L-38.9878086,157.745149 C-38.1842067,108.18132 2.24466689,68.25 52,68.25 Z"
                id="Path-1"
                fill="#FF3239"
                transform="translate(52.000000, 113.750000) rotate(-120.000000) translate(-52.000000, -113.750000) "
              />
            </g>
            <g mask="url(#mask)">
              <path
                d="M130.808312,68.25 C180.563645,68.25 220.992518,108.18132 221.79612,157.745149 L221.808312,159.25 L189.808312,159.25 C189.808312,126.6652 163.393112,100.25 130.808312,100.25 C98.2235115,100.25 71.8083117,126.6652 71.8083117,159.25 L39.8083117,159.25 L39.8205031,157.745149 C40.6241051,108.18132 81.0529786,68.25 130.808312,68.25 Z"
                id="Path-2"
                fill="#04BF68"
                transform="translate(130.808312, 113.750000) rotate(120.000000) translate(-130.808312, -113.750000) "
              />
            </g>
          </g>
          <g
            id="Website@introduction"
            stroke="none"
            strokeWidth="1"
            fill="none"
            fillRule="evenodd"
            x="91"
            y="91"
            transform={`rotate(${rotation} 91 91)`}
          >
            <line
              x1="25"
              y1="91"
              x2="91"
              y2="91"
              id="Line-3"
              stroke="white"
              strokeWidth="8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <line
              x1="25"
              y1="91"
              x2="91"
              y2="91"
              id="Line-3"
              stroke="#71717A"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle id="Oval" fill="#3F3F46" cx="91" cy="91" r="8" />
          </g>
        </svg>
      </Box>
    </Card>
  );
};

export interface ISalesGoalCardProps extends ICardProps {
  data: object[];
  keys: string[];
  indexBy: string;
  description: string;
  formatting?: NumberFormatOptions & { format?: string };
  value?: number;
}

export const SalesGoalCard: React.FC<ISalesGoalCardProps> = ({
  data,
  keys,
  indexBy,
  description,
  formatting,
  value,
  ...props
}) => {
  const intl = useIntl();
  return (
    <Card minHeight="200px" padding={majorScale(2)} flexGrow={1} {...props}>
      <Box is="p">{description}</Box>
      {value && (
        <Heading is="p" size={400} className="font-mono medium large">
          <FormattedNumber
            format="short"
            maximumFractionDigits={0}
            minimumFractionDigits={0}
            {...formatting}
            value={value}
          />
        </Heading>
      )}
      <Box height="180px">
        <ResponsiveBar
          groupMode="grouped"
          data={data}
          keys={keys}
          indexBy={indexBy}
          margin={{
            top: majorScale(2),
            bottom: majorScale(3),
            left: majorScale(0),
            right: majorScale(12),
          }}
          valueScale={{ type: 'linear' }}
          axisTop={null}
          axisLeft={null}
          padding={0.2}
          axisBottom={{
            format: (v) => moment(v).format('L'),
          }}
          axisRight={{
            tickPadding: 4,
            tickSize: 0,
            tickValues: 4,
            orient: 'right',
            legendOffset: -40,
            legendPosition: 'middle',
            format: (v) =>
              intl.formatNumber(v as number, {
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
                ...formatting,
              }),
          }}
          enableLabel={false}
          enableGridY={true}
          gridYValues={4}
          colors={[defaultFills.success[500], defaultFills.muted]}
          tooltip={(datum: BarTooltipDatum) => (
            <Card background="white" border="none">
              <p className="font-mono">
                <FormattedNumber
                  format="currency"
                  maximumFractionDigits={0}
                  minimumFractionDigits={0}
                  value={datum.value}
                />
              </p>
              <p
                className="small muted"
                style={{ textTransform: 'capitalize' }}
              >
                {datum.id}
              </p>
            </Card>
          )}
        />
      </Box>
    </Card>
  );
};
